/**
 * The state transitions, written once and run on both sides.
 *
 * The server applies an event to its copy and broadcasts it; every subscribed client
 * applies the identical function to theirs. Sharing the code is the point — in the 2023
 * build the server and the client each had their own path into match state, and they
 * could disagree without either being detectably wrong.
 *
 * Every event is self-describing (`ev` is part of the object), so the wire carries one
 * value and the reducer switches on it exhaustively. A new event that nobody handles is
 * a type error rather than a silent no-op.
 */

import type { MatchStatus, PlayerIndex } from '@gok/engine';
import type {
  Challenge,
  ChatLine,
  LobbyState,
  MatchState,
  MatchSummary,
  PlayedMove,
  Rating,
  UserState,
} from './wire.ts';

// --- lobby ----------------------------------------------------------------

export type LobbyEvent =
  | { ev: 'join'; userId: string }
  | { ev: 'leave'; userId: string }
  | { ev: 'challenge'; challenge: Challenge }
  | { ev: 'unchallenge'; id: string }
  | { ev: 'matchStarted'; match: MatchSummary }
  | { ev: 'matchEnded'; matchId: string; status: MatchStatus };

/** How many finished matches the lobby remembers. */
export const RECENT_MATCH_LIMIT = 16;

export const EMPTY_LOBBY: LobbyState = {
  users: [],
  challenges: [],
  live: [],
  recent: [],
};

export function reduceLobby(state: LobbyState, e: LobbyEvent): LobbyState {
  switch (e.ev) {
    case 'join':
      return state.users.includes(e.userId)
        ? state
        : { ...state, users: [...state.users, e.userId] };

    case 'leave':
      // A challenge is an offer to play *now*, so it leaves with its challenger. This
      // is also why challenges are not persisted: they cannot outlive a connection.
      return {
        ...state,
        users: state.users.filter((u) => u !== e.userId),
        challenges: state.challenges.filter((c) => c.challengerId !== e.userId),
      };

    case 'challenge':
      return {
        ...state,
        // One open challenge per player: posting a second replaces the first.
        challenges: [
          ...state.challenges.filter((c) => c.challengerId !== e.challenge.challengerId),
          e.challenge,
        ],
      };

    case 'unchallenge':
      return { ...state, challenges: state.challenges.filter((c) => c.id !== e.id) };

    case 'matchStarted':
      return state.live.some((m) => m.id === e.match.id)
        ? state
        : { ...state, live: [e.match, ...state.live] };

    case 'matchEnded': {
      const ended = state.live.find((m) => m.id === e.matchId);
      return {
        ...state,
        live: state.live.filter((m) => m.id !== e.matchId),
        // An aborted match is one nobody played; it is not worth remembering.
        recent: e.status === 'aborted' || !ended
          ? state.recent
          : [ended, ...state.recent.filter((m) => m.id !== e.matchId)]
            .slice(0, RECENT_MATCH_LIMIT),
      };
    }
  }
}

// --- user -----------------------------------------------------------------

export type UserEvent = {
  ev: 'update';
  username?: string;
  rating?: Rating;
  registered?: boolean;
};

export function reduceUser(state: UserState, e: UserEvent): UserState {
  switch (e.ev) {
    case 'update':
      return {
        ...state,
        ...(e.username === undefined ? {} : { username: e.username }),
        ...(e.rating === undefined ? {} : { rating: e.rating }),
        ...(e.registered === undefined ? {} : { registered: e.registered }),
      };
  }
}

// --- match ----------------------------------------------------------------

export type MatchEvent =
  /** A move landed. Carries the server's clock reading so nobody recomputes it. */
  | {
    ev: 'move';
    move: PlayedMove;
    clocks: readonly [number, number];
    turnStartedAt: number;
    status: MatchStatus;
    winner: PlayerIndex | null;
  }
  /** The match ended without a move: resignation, flag fall, abort. */
  | {
    ev: 'end';
    status: MatchStatus;
    winner: PlayerIndex | null;
    clocks: readonly [number, number];
  }
  | { ev: 'chat'; line: ChatLine };

export function reduceMatch(state: MatchState, e: MatchEvent): MatchState {
  switch (e.ev) {
    case 'move':
      return {
        ...state,
        moves: [...state.moves, e.move],
        clocks: e.clocks,
        turnStartedAt: e.turnStartedAt,
        status: e.status,
        winner: e.winner,
      };

    case 'end':
      return { ...state, status: e.status, winner: e.winner, clocks: e.clocks };

    case 'chat':
      return { ...state, chat: [...state.chat, e.line] };
  }
}

// --- channel names --------------------------------------------------------

export const LOBBY = 'lobby';
export const userChannel = (userId: string): string => `user-${userId}`;
export const matchChannel = (matchId: string): string => `match-${matchId}`;

export type ChannelKind = 'lobby' | 'user' | 'match';

/** Split a validated channel name into its kind and subject id. */
export function parseChannel(ch: string): { kind: ChannelKind; id: string } | undefined {
  if (ch === LOBBY) return { kind: 'lobby', id: '' };
  const dash = ch.indexOf('-');
  if (dash < 0) return undefined;
  const kind = ch.slice(0, dash);
  const id = ch.slice(dash + 1);
  if (!id) return undefined;
  if (kind === 'user' || kind === 'match') return { kind, id };
  return undefined;
}
